import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { LogOut, ShieldOff } from "lucide-react";
import { ROLE_LABELS } from "@ir/types";
import { Button } from "@ir/ui";
import { useAuth } from "../auth/AuthProvider";

/**
 * Shown when a signed-in user reaches a page their role does not cover. It
 * deliberately describes nothing about the page they were trying to reach.
 */
export function NoAccessPage() {
  const { user, signOut } = useAuth();

  return (
    <div className="grid min-h-[70dvh] place-items-center px-4">
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-md text-center"
      >
        <span className="mx-auto mb-5 grid size-16 place-items-center rounded-2xl border border-line bg-surface-sunken text-content-subtle">
          <ShieldOff size={28} aria-hidden="true" />
        </span>
        <h1 className="font-display text-xl font-semibold text-content">
          You do not have access to this page
        </h1>
        <p className="mt-2 text-sm text-content-muted">
          You are signed in as{" "}
          <span className="font-medium text-content">{user?.email}</span>
          {user && <> with {ROLE_LABELS[user.role].toLowerCase()} access</>}. If
          this is wrong, ask an administrator to update your role.
        </p>

        <div className="mt-7 flex flex-col justify-center gap-2 sm:flex-row">
          <Button variant="secondary" onClick={() => void signOut()} icon={LogOut}>
            Sign out
          </Button>
          <Link to="/">
            <Button className="w-full sm:w-auto">Back to overview</Button>
          </Link>
        </div>
      </motion.div>
    </div>
  );
}

export function NotFoundPage() {
  return (
    <div className="grid min-h-[70dvh] place-items-center px-4">
      <div className="max-w-md text-center">
        <p className="font-display text-5xl font-semibold text-gold-gradient">404</p>
        <h1 className="mt-3 font-display text-xl font-semibold text-content">
          That page does not exist
        </h1>
        <p className="mt-2 text-sm text-content-muted">
          The link may be out of date, or the page may have moved.
        </p>
        <Link to="/" className="mt-6 inline-block">
          <Button>Back to overview</Button>
        </Link>
      </div>
    </div>
  );
}
